import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import FormData from "form-data";
import { getStores } from "./../redux/actions/action-creator";

import Footer from "../components/Footer";
import BottomFooter from "../components/BottomFooter";
import Newsletter from "../components/Newsletter";
import ScrollToTop from "react-scroll-to-top";
import ColorInit from "../helper/ColorInit";

import Header from "../components/Header";
import ShoppingBreadcrumb from "../components/ShoppingBreadcrumb";
import MarketPromoBanner from "../components/MarketPromoBanner";
import MobileCatalogSearch from "../components/MobileCatalogSearch";
import TopVendors from "../components/TopVendors";
import {
  DEFAULT_DELIVERY_LOCATION,
  LOCATION_STORAGE_KEY,
} from "../utils/locationDefaults";
import { setPageSeo } from "../utils/seo";
import "./HomePage.css";
import { getSegmentId, getSegmentItems, isSeparateServiceSegment } from "../utils/segmentFlow";

const DEFAULT_LOCATION = DEFAULT_DELIVERY_LOCATION;

const SuperMarketsPage = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeLocation = useLocation();
  const { segmentId: routeSegmentId } = useParams();
  const servicesData = useSelector((state) => state.common?.servicesData);
  const activeSegmentId = routeSegmentId || searchParams.get("segment_id");
  const selectedSegment = routeLocation.state?.segment || getSegmentItems(servicesData).find(
    (item) => String(getSegmentId(item)) === String(activeSegmentId)
  );
  const separateService = isSeparateServiceSegment(selectedSegment);
  const { latitude, longitude, country_id, area, merchant_id, user_location } = useSelector(
    (state) => state.auth
  );
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => setSearchTerm(""), [activeSegmentId]);

  const getSavedUserLocation = useCallback(() => {
    try {
      const savedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
      return savedLocation ? JSON.parse(savedLocation) : null;
    } catch (error) {
      return null;
    }
  }, []);

  const getActiveUserLocation = useCallback(() => {
    const savedLocation = getSavedUserLocation();
    return {
      latitude:
        latitude ??
        user_location?.latitude ??
        savedLocation?.latitude ??
        DEFAULT_LOCATION.latitude,
      longitude:
        longitude ??
        user_location?.longitude ??
        savedLocation?.longitude ??
        DEFAULT_LOCATION.longitude,
      country_id:
        country_id ??
        user_location?.country_id ??
        user_location?.countryId ??
        savedLocation?.country_id ??
        savedLocation?.countryId ??
        DEFAULT_LOCATION.country_id,
      merchant_id:
        merchant_id ??
        user_location?.merchant_id ??
        user_location?.merchantId ??
        savedLocation?.merchant_id ??
        savedLocation?.merchantId ??
        DEFAULT_LOCATION.merchant_id,
      area:
        area ??
        user_location?.area ??
        user_location?.area_id ??
        savedLocation?.area ??
        savedLocation?.area_id ??
        null,
    };
  }, [area, country_id, latitude, longitude, merchant_id, user_location, getSavedUserLocation]);

  useEffect(() => {
    if (separateService) {
      navigate("/home", { replace: true });
      return;
    }
    loadHomeData();
  }, [searchParams, routeSegmentId, latitude, longitude, country_id, area, merchant_id, user_location, getSavedUserLocation, separateService, navigate]);

  useEffect(() => {
    setPageSeo({
      title: "Grocery Stores Near You | RGHT NOW",
      description: "Browse nearby RGHT NOW stores based on your selected delivery location.",
      path: routeLocation.pathname,
    });
  }, [routeLocation.pathname]);

  const loadHomeData = async () => {
    try {

      setLoading(true);
      
      const segmentId = routeSegmentId || searchParams.get("segment_id");
      console.log("StoresPage - segment_id from URL:", segmentId);

      const userLocation = getActiveUserLocation();
      
      /*NEAR STORE DATA*/
      let storeData = new FormData();
      storeData.append("is_dev_stores_enable", 1);
      storeData.append("latitude", userLocation.latitude);
      storeData.append("longitude", userLocation.longitude);
      storeData.append("merchant_id", userLocation.merchant_id);
      storeData.append("country_id", userLocation.country_id);
      if (userLocation.area) {
        storeData.append("area", userLocation.area);
      }
      storeData.append("home_screen", 1);
      storeData.append("banner_for", 1);
      storeData.append("segment_id", segmentId || "");
      //storeData.append("delivery_areas", [10, 17, 18, 22, 28, 29, 49, 50, 80, 81, 82, 85, 86, 87, 88]);

      // FIRST API
      const response = await dispatch(getStores(storeData));

      if (response?.result) {

        setStores(response.data);
      }

    } catch (error) {
      setLoading(false);
      console.log("API Error:", error.message);
    } finally {
      setLoading(false);
    }
  };

  return (

    <div className="bg-white home-page-shell stores-page-shell">

      {/* ScrollToTop */}
      <ScrollToTop smooth color="#299E60" />

      {/* ColorInit */}
      <ColorInit color={false} />

      {/* Header */}
      <Header mobileHomeHeader mobileHeaderTitle="Stores" />

      {/* Breadcrumb */}
      <ShoppingBreadcrumb page="stores" segment={selectedSegment} segmentId={activeSegmentId} />

      <MarketPromoBanner page="stores" segmentId={activeSegmentId} enabled={!separateService} />
      <MobileCatalogSearch value={searchTerm} onChange={setSearchTerm} placeholder="Search for stores" />

      {/* Stores */}
      {/*<Stores />*/}

      {/* TopVendors */}
      <TopVendors stores={stores} loading={loading} searchTerm={searchTerm} segment={selectedSegment} segmentId={activeSegmentId}/>

      {/* Newsletter */}
      <Newsletter />

      {/* Footer */}
      <Footer />
      
      {/* BottomFooter */}
      <BottomFooter mobileAppNav />


    </div>
  );
};

export default SuperMarketsPage;
